
import React, { useState } from 'react';
import { Plus, Trash2, Layers, Search, PencilIcon, Check, X } from 'lucide-react';
import { Collections, Product } from '../../types';
import ConfirmationModal from '../UI/ConfirmationModal';
import { useToast } from '../../contexts/ToastContext';

interface CollectionsViewProps {
    collections: Collections[];
    products: Product[];
    onAddCollection: (collection: Collections) => void;
    onEditCollection: (collection: Collections) => void;
    onDeleteCollection: (collection: Collections) => void;
}

const CollectionsView: React.FC<CollectionsViewProps> = ({
    collections = [],
    products = [],
    onAddCollection,
    onEditCollection,
    onDeleteCollection,
}) => {
    const [newCollectionName, setNewCollectionName] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [collectionToDelete, setCollectionToDelete] = useState<Collections | null>(null);
    const [editingCollection, setEditingCollection] = useState<Collections | null>(null);
    const [editName, setEditName] = useState('');
    const { addToast } = useToast();

    // DELETE collection
    const handleDeleteCollection = async () => {
        if (!collectionToDelete) return;
        try {
            const res = await fetch(`${process.env.COLLECTION_BASE_URL}/${collectionToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
            });
            const data = await res.json();
            if (data.OK) {
                onDeleteCollection(collectionToDelete);
                setCollectionToDelete(null);
                addToast('success', 'Collection Removed', `"${collectionToDelete.name}" deleted.`);
            } else {
                setCollectionToDelete(null);
                addToast('error', 'Cannot Delete', data.MSG ?? 'Failed to delete collection.');
            }
        } catch {
            addToast('error', 'Network Error', 'Could not reach the server. Try again.');
        }
    };

    // EDIT / RENAME collection
    const handleSaveEdit = async (coll: Collections) => {
        const trimmed = editName.trim();
        if (!trimmed) return;
        try {
            const res = await fetch(`${process.env.COLLECTION_BASE_URL}/${coll.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ collectionName: trimmed }),
            });
            const data = await res.json();
            if (data.OK) {
                const raw = data.DATA;
                onEditCollection({ _id: raw.collectionId, id: raw.collectionId, name: raw.collectionName });
                setEditingCollection(null);
                addToast('success', 'Collection Renamed', `Now: "${raw.collectionName}"`);
            } else {
                addToast('error', 'Rename Failed', data.MSG ?? 'Could not rename collection.');
            }
        } catch {
            addToast('error', 'Network Error', 'Could not reach the server. Try again.');
        }
    };

    // CREATE collection
    const handleAddColl = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = newCollectionName.trim();
        if (!trimmed) return;
        try {
            const res = await fetch(process.env.COLLECTION_BASE_URL!, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ collectionName: trimmed }),
            });
            const data = await res.json();
            if (data.OK) {
                const raw = data.DATA;
                onAddCollection({ _id: raw.collectionId, id: raw.collectionId, name: raw.collectionName });
                setNewCollectionName('');
                addToast('success', 'Collection Created', `"${raw.collectionName}" is ready.`);
            } else {
                addToast('error', 'Create Failed', data.MSG ?? 'Could not create collection.');
            }
        } catch {
            addToast('error', 'Network Error', 'Could not reach the server. Try again.');
        }
    };

    const filteredCollections = collections.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-8 h-full overflow-y-auto bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
            <div className="max-w-5xl mx-auto space-y-6">
                <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Collections Management</h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Define product streams and seasonal collections.</p>
                    </div>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        <input
                            type="text"
                            placeholder="Search collections..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500 w-64 transition-all"
                        />
                    </div>
                </header>

                {/* Add new collection */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <form onSubmit={handleAddColl} className="flex gap-3">
                        <div className="relative flex-1">
                            <Layers className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                required
                                type="text"
                                maxLength={60}
                                value={newCollectionName}
                                onChange={(e) => setNewCollectionName(e.target.value)}
                                placeholder="Enter new collection name"
                                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:text-white transition-all"
                            />
                        </div>
                        {/* BUG #12 fix: use type="button" + onClick so both click and Enter work */}
                        <button
                            type="button"
                            onClick={handleAddColl as any}
                            disabled={!newCollectionName.trim()}
                            className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center gap-2 shadow-lg shadow-indigo-100 dark:shadow-none"
                        >
                            <Plus size={18} />
                            <span>Create</span>
                        </button>
                    </form>
                </div>

                {/* Collection grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredCollections.map((coll) => {
                        const count = products.filter(p => p.collection === coll.name).length;
                        const isEditing = editingCollection?.id === coll.id;
                        return (
                            <div
                                key={coll.id}
                                className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between group hover:border-indigo-300 dark:hover:border-indigo-700 transition-all"
                            >
                                <div className="flex items-center gap-4 min-w-0 flex-1">
                                    <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
                                        <Layers size={20} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        {isEditing ? (
                                            <div className="flex items-center gap-2">
                                                <input
                                                    autoFocus
                                                    value={editName}
                                                    onChange={(e) => setEditName(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleSaveEdit(coll);
                                                        if (e.key === 'Escape') setEditingCollection(null);
                                                    }}
                                                    className="w-full px-2 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-sm font-bold text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                                                />
                                                <button
                                                    onClick={() => handleSaveEdit(coll)}
                                                    className="p-1 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded"
                                                >
                                                    <Check size={16} />
                                                </button>
                                                <button
                                                    onClick={() => setEditingCollection(null)}
                                                    className="p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <h3 className="font-bold text-gray-900 dark:text-white truncate">{coll.name}</h3>
                                                <p className="text-xs text-gray-400 dark:text-gray-500 font-medium">{count} Articles</p>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {!isEditing && (
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => { setEditingCollection(coll); setEditName(coll.name); }}
                                            className="p-2 text-gray-300 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                        >
                                            <PencilIcon size={16} />
                                        </button>
                                        <button
                                            onClick={() => setCollectionToDelete(coll)}
                                            className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {filteredCollections.length === 0 && (
                    <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
                        <Layers size={48} className="mx-auto mb-4 text-gray-200 dark:text-gray-700" />
                        <p className="text-gray-400 dark:text-gray-500 text-sm">No collections found.</p>
                    </div>
                )}
            </div>

            <ConfirmationModal
                isOpen={!!collectionToDelete}
                onClose={() => setCollectionToDelete(null)}
                onConfirm={handleDeleteCollection}
                title="Remove Collection?"
                message={`Are you sure you want to delete "${collectionToDelete?.name}"? Products in this collection will become uncategorized.`}
                confirmText="Remove"
                type="danger"
            />
        </div>
    );
};

export default CollectionsView;
